<template>
  <ModalContainer :is-show-modal="isShowModal" @close="closeModal">
    <template #header>
      <div>{{ $t("Ask Us a Question") }}</div>
      <IconButton icon="cross" @click="closeModal" />
    </template>

    <form class="modal-form" @submit.prevent="onSubmit">
      <div class="modal-form-body">
        <BaseInput
          v-model="state.name"
          :placeholder="$t('Name')"
          :inner-placeholder="$t('Your name')"
          :error="errors?.name?.[0]"
        />
        <BaseInput
          v-model="state.phone"
          label="phone"
          :placeholder="$t('Phone')"
          :inner-placeholder="phoneNumberFormat[countryCode].placeholder"
          :error="errors?.phone?.[0]"
          :validator="validators.phone.phoneValidator(countryCode)"
          :unavailable-symbols="validators.phone.unavailableSymbols"
          @is-error="validationErrors['phone']"
          type="tel"
          :help-text="$t('contacts.formatNumber')"
          :trim="true"
        />
        <BaseInput
          v-model="state.email"
          label="email"
          type="email"
          :placeholder="$t('Email')"
          :inner-placeholder="emailPlaceholder"
          :error="errors?.email?.[0]"
          :validator="validators.email"
          @is-error="validationErrors['email']"
          :trim="true"
        />
        <BaseTextarea v-model="state.question" :placeholder="$t('Question')" />
        <AgreementCheckbox v-model="state.processPersonalData" />
      </div>
      <div class="modal-form-control">
        <RecaptchaV2Fallback v-if="showV2Captcha" @verify="onV2Verify" />
        <BaseButton
          type="submit"
          :loading="loading"
          :disabled="isDisabledButton || showV2Captcha"
        >
          {{ $t("Send") }}
        </BaseButton>
      </div>
    </form>

    <div class="modal-body__separator">
      <hr />
      <span class="text-18-500">{{ $t("or") }}</span>
      <hr />
    </div>

    <a class="modal-body__phone" :href="`tel:${officeContact.phone}`">
      <BaseIcon icon="phone" />
      <span class="text-18-500">{{ officeContact.phone }}</span>
    </a>
  </ModalContainer>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import IconButton from "../button/IconButton.vue";
import { useConfigStore } from "~/stores/config";
import useInputValidation from "~/composables/useInputValidation";
import { submitRecaptcha } from "~/api/formRequests";
import { phoneNumberFormat } from "~/utils/validators/phone";
import ModalContainer from "~/components/modals/ModalContainer.vue";
import BaseInput from "~/components/input/BaseInput.vue";
import validators from "~/utils/validators";
import AgreementCheckbox from "~/components/agreementCheckbox/AgreementCheckbox.vue";
import BaseButton from "~/components/button/BaseButton.vue";
import BaseTextarea from "~/components/input/BaseTextarea.vue";
import BaseIcon from "~/components/icons/BaseIcon.vue";
import { successModalKey } from "~/symbols";
import { useContentStore } from "~/stores/content";
import { SuccessModalTypes } from "~/types/entities";
import { useReCaptcha } from "vue-recaptcha-v3";
import RecaptchaV2Fallback from "~/components/recaptcha/V2Fallback.vue";
import getEmailPlaceHolder from "~/utils/getEmailPlaceholder.js";

const emit = defineEmits(["update:isShowModal"]);

defineProps({
  isShowModal: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
});

const contentStore = useContentStore();
const { getContacts: officeContact } = storeToRefs(contentStore);

const { brand } = useRuntimeConfig().public;

const { countryCode } = storeToRefs(useConfigStore());

const defaultState = {
  name: "",
  phone: "",
  email: "",
  question: "",
  processPersonalData: false,
  country: countryCode.value,
};

const state = reactive({
  ...defaultState,
});

const loading = ref(false);
const errors = ref<any>(null);
const showV2Captcha = ref(false);

const { executeRecaptcha, recaptchaLoaded } = useReCaptcha();

const { isValid, validationErrors } = useInputValidation(state, { optionalFields: ["phone"] });
const isDisabledButton = computed(() => !isValid.value || !!errors.value);

const preparedPhone = computed(() => removePlusSymbol(state.phone));

const showSuccessModal = inject(successModalKey, () => {});

const emailPlaceholder = getEmailPlaceHolder(brand);

watch(
  () => state,
  () => {
    if (errors.value) {
      errors.value = null;
    }
  },
  { deep: true }
);
function closeModal() {
  resetState();
  emit("update:isShowModal", false);
}
function resetState() {
  Object.assign(state, defaultState);
  showV2Captcha.value = false;
}
async function onSubmit() {
  loading.value = true;
  try {
    const token = await getV3Token();
    const { success } = await submitRecaptcha({
      form: { ...state, phone: preparedPhone.value },
      token,
      formType: "question",
    });

    if (!success) {
      showV2Captcha.value = true;
      return;
    }

    closeModal();
    showSuccessModal(SuccessModalTypes.QUESTION);
  } catch (error: any) {
    handleSubmitError(error);
  } finally {
    loading.value = false;
  }
}
async function onV2Verify(v2Token: string) {
  loading.value = true;
  try {
    const { success } = await submitRecaptcha({
      form: { ...state, phone: preparedPhone.value },
      v2Token,
      formType: "question",
    });

    if (!success) {
      throw new Error("reCAPTCHA verification failed");
    }

    closeModal();
    showSuccessModal(SuccessModalTypes.QUESTION);
  } catch (error: any) {
    handleSubmitError(error);
  } finally {
    loading.value = false;
  }
}
function handleSubmitError(error: any) {
  if (error?.data?.errors) {
    errors.value = error.data.errors;
  }
  closeModal();
  showSuccessModal(error?.data?.modalType || SuccessModalTypes.ISSUE);
}
async function getV3Token() {
  await recaptchaLoaded();
  const token = await executeRecaptcha("submit");
  if (!token) throw new Error("reCAPTCHA token missing");
  return token;
}
</script>
